import React, { useState, useEffect } from 'react';
import { FaSignInAlt, FaBars, FaTimes } from 'react-icons/fa';
import dbohraLogo from '../images/Dbohra Logo.png';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isArticlePage, setIsArticlePage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if current page is a blogs/article page
    const currentPath = window.location.pathname;
    const isBlogPage = currentPath.includes('/blogs/') && currentPath !== '/blogs';
    setIsArticlePage(isBlogPage);

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Add/remove body class for fixed header styling on mobile
    if (isMobile) {
      document.body.classList.add('has-fixed-header');
    } else {
      document.body.classList.remove('has-fixed-header');
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      document.body.classList.remove('has-fixed-header');
    };
  }, [isMobile, isArticlePage]);

  // Mobile-specific styles for consistent mobile experience
  const getMobileStyles = () => {
    if (isMobile) {
      return {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: 1001,
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(15px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        padding: '0.5rem 0',
        minHeight: '60px',
        maxHeight: '60px'
      };
    }
    return {};
  };

  // Logo container styles for consistent mobile experience
  const getLogoContainerStyles = () => {
    if (isMobile) {
      return {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'transform 0.3s ease',
        height: '40px',
        minHeight: '40px',
        maxHeight: '40px',
        position: 'absolute',
        left: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1002
      };
    }
    return {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'transform 0.3s ease',
      height: '40px',
      minHeight: '40px',
      maxHeight: '40px'
    };
  };

  // Row styles for consistent mobile experience
  const getRowStyles = () => {
    if (isMobile) {
      return {
        minHeight: '60px',
        maxHeight: '60px',
        position: 'relative'
      };
    }
    return {
      minHeight: '40px',
      maxHeight: '40px'
    };
  };

  // Mobile menu button styles for consistent mobile experience
  const getMobileMenuButtonStyles = () => {
    if (isMobile) {
      return {
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        color: '#1f2937',
        cursor: 'pointer',
        padding: '0.5rem',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        position: 'absolute',
        right: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1002
      };
    }
    return {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: '#1f2937',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '8px',
      transition: 'all 0.3s ease'
    };
  };

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      padding: '1rem 0',
      minHeight: '72px',
      maxHeight: '72px',
      ...getMobileStyles()
    }}>
      <div className="container">
        <div className="row align-items-center" style={getRowStyles()}>
          {/* Logo - Always visible */}
          <div className="col-md-4 col-6">
            <div style={getLogoContainerStyles()} onClick={() => window.location.href = '/'}>
              <img 
                src={dbohraLogo}
                alt="DBohra Logo"
                style={{
                  height: '40px',
                  width: 'auto',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              />
            </div>
          </div>

          {/* Desktop Navigation Links - Hidden on mobile */}
          <div className="col-md-4 d-none d-md-block">
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1.5rem',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}>
              <a href="/" style={{
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.95rem',
                transition: 'color 0.3s ease',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                whiteSpace: 'nowrap'
              }}>Home</a>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <a href="/publications" style={{
                  textDecoration: 'none',
                  color: '#1f2937',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  transition: 'color 0.3s ease',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>Publications</a>
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  background: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  minWidth: '200px',
                  zIndex: 1000,
                  opacity: 0,
                  visibility: 'hidden',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(-10px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '1';
                  e.target.style.visibility = 'visible';
                  e.target.style.transform = 'translateY(0)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '0';
                  e.target.style.visibility = 'hidden';
                  e.target.style.transform = 'translateY(-10px)';
                }}>
                  <a href="/research-papers" style={{
                    display: 'block',
                    padding: '0.75rem 1rem',
                    textDecoration: 'none',
                    color: '#1f2937',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                    📄 Research Papers
                  </a>
                  <a href="/newsletters" style={{
                    display: 'block',
                    padding: '0.75rem 1rem',
                    textDecoration: 'none',
                    color: '#1f2937',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                    📰 Newsletters
                  </a>
                                      <a href="/blogs" style={{
                      display: 'block',
                      padding: '0.75rem 1rem',
                      textDecoration: 'none',
                      color: '#1f2937',
                      transition: 'background-color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                      📝 Blogs
                    </a>
                </div>
              </div>
              <a href="/weekly-highlights" style={{
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.95rem',
                transition: 'color 0.3s ease',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                whiteSpace: 'nowrap'
              }}>Weekly Highlights</a>
              <a href="/about" style={{
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.95rem',
                transition: 'color 0.3s ease',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                whiteSpace: 'nowrap'
              }}>About Us</a>
              <a href="/contact" style={{
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.95rem',
                transition: 'color 0.3s ease',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                whiteSpace: 'nowrap'
              }}>Contact Us</a>
            </div>
          </div>

          {/* Desktop Sign In Button - Hidden on mobile */}
          <div className="col-md-4 text-end d-none d-md-block">
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}>
              <button style={{
                background: '#6366f1',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: '500',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onClick={() => window.location.href = '/login'}>
                <FaSignInAlt style={{ marginRight: '6px' }} />Sign In
              </button>
            </div>
          </div>

          {/* Mobile Burger Menu Button - Visible only on mobile */}
          <div className="col-6 text-end d-md-none">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={getMobileMenuButtonStyles()}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Hidden by default, shown when burger is clicked */}
        <div className={`d-md-none ${isMobileMenuOpen ? 'd-block' : 'd-none'}`} style={{
          marginTop: '1rem',
          padding: '1rem',
          background: 'rgba(255, 255, 255, 0.98)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            <a href="/" style={{
              textDecoration: 'none',
              color: '#1f2937',
              fontWeight: '500',
              fontSize: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              border: '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
              e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'transparent';
            }}
            onClick={() => setIsMobileMenuOpen(false)}>
              🏠 Home
            </a>
            <div style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
              <div style={{ 
                padding: '0.75rem 1rem', 
                color: '#6b7280', 
                fontSize: '0.9rem', 
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                📚 Publications
              </div>
              <a href="/publications" style={{
                display: 'block',
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.9rem',
                padding: '0.5rem 1rem 0.5rem 2rem',
                transition: 'all 0.3s ease',
                border: '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              onClick={() => setIsMobileMenuOpen(false)}>
                📚 Publications
              </a>
              <a href="/research-papers" style={{
                display: 'block',
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.9rem',
                padding: '0.5rem 1rem 0.5rem 2rem',
                transition: 'all 0.3s ease',
                border: '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              onClick={() => setIsMobileMenuOpen(false)}>
                📄 Research Papers
              </a>
              <a href="/newsletters" style={{
                display: 'block',
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.9rem',
                padding: '0.5rem 1rem 0.5rem 2rem',
                transition: 'all 0.3s ease',
                border: '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(99, 241, 99, 0.1)';
                e.target.style.borderColor = 'rgba(99, 241, 99, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              onClick={() => setIsMobileMenuOpen(false)}>
                📰 Newsletters
              </a>
              <a href="/blogs" style={{
                display: 'block',
                textDecoration: 'none',
                color: '#1f2937',
                fontWeight: '500',
                fontSize: '0.9rem',
                padding: '0.5rem 1rem 0.5rem 2rem',
                transition: 'all 0.3s ease',
                border: '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(241, 99, 99, 0.1)';
                e.target.style.borderColor = 'rgba(241, 99, 99, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = 'transparent';
              }}
              onClick={() => setIsMobileMenuOpen(false)}>
                📝 Blogs
              </a>
            </div>
            <a href="/weekly-highlights" style={{
              textDecoration: 'none',
              color: '#1f2937',
              fontWeight: '500',
              fontSize: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              border: '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
              e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'transparent';
            }}
            onClick={() => setIsMobileMenuOpen(false)}>
              📰 Weekly Highlights
            </a>
            <a href="/about" style={{
              textDecoration: 'none',
              color: '#1f2937',
              fontWeight: '500',
              fontSize: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              border: '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
              e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'transparent';
            }}
            onClick={() => setIsMobileMenuOpen(false)}>
              ℹ️ About Us
            </a>
            <a href="/contact" style={{
              textDecoration: 'none',
              color: '#1f2937',
              fontWeight: '500',
              fontSize: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              border: '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
              e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'transparent';
            }}
            onClick={() => setIsMobileMenuOpen(false)}>
              📞 Contact Us
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;


